import React, { createContext, useContext, useState, useEffect } from "react";
import { authAPI } from "../api";

interface User {
  id: number;
  fullName: string;
  email: string;
  plan: "free" | "premium";
  role?: string;
  unique_code?: string | null;
  is_onboarded?: boolean;
  telegram_username?: string | null;
}
interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (
    name: string,
    email: string,
    password: string,
    confirm_password: string,
    role?: string,
  ) => Promise<void>;
  logout: () => void;
  loading: boolean;
  setAuth: (token: string, user: User, refreshToken?: string) => void;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

const parseResponse = (data: any) => {
  return {
    access: data?.access_token || "",
    refresh: data?.refresh_token || "",
    user: data?.user || {},
  };
};

const normalizeUser = (rawUser: any, fallback: Partial<User> = {}): User => ({
  id: rawUser?.id || 0,
  fullName:
    rawUser?.name ||
    rawUser?.fullName ||
    rawUser?.full_name ||
    fallback.fullName ||
    "",
  email: rawUser?.email || fallback.email || "",
  plan: rawUser?.plan || rawUser?.subscription || "free",
  role: rawUser?.role,
  unique_code: rawUser?.unique_code || null,
  is_onboarded: rawUser?.is_onboarded ?? false,
  telegram_username: rawUser?.telegram_username || null,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(
    localStorage.getItem("token"),
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser && token) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {}
    }
    setLoading(false);
  }, [token]);

  const saveTokens = (access: string, refresh: string, userData: User) => {
    localStorage.setItem("token", access);
    if (refresh) localStorage.setItem("refresh_token", refresh);
    localStorage.setItem("user", JSON.stringify(userData));
    setToken(access);
    setUser(userData);
  };

  const login = async (email: string, password: string) => {
    const res = await authAPI.signIn(email, password);
    const { access, refresh, user: rawUser } = parseResponse(res.data);
    const newUser = normalizeUser(rawUser, { email });
    saveTokens(access, refresh, newUser);
    return res.data;
  };

  const updateUser = (updates: Partial<User>) => {
    const updated = { ...user, ...updates } as User;
    setUser(updated);
    localStorage.setItem("user", JSON.stringify(updated));
  };

  const register = async (
    name: string,
    email: string,
    password: string,
    confirm_password: string,
    role?: string,
  ) => {
    const res = await authAPI.signUp(
      name,
      email,
      password,
      confirm_password,
      role,
    );
    const { access, refresh, user: rawUser } = parseResponse(res.data);
    const newUser = normalizeUser(rawUser, { fullName: name, email });
    saveTokens(access, refresh, newUser);
    return res.data;
  };

  const setAuth = (newToken: string, newUser: User, refreshToken?: string) => {
    saveTokens(newToken, refreshToken || "", newUser);
  };

  const logout = async () => {
    try {
      // backend'ga logout chaqirish (refresh tokenni o'chiradi)
      await authAPI.logout();
    } catch (e) {
      console.warn("Logout API error:", e);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      setToken(null);
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        register,
        logout,
        loading,
        setAuth,
        updateUser,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
